import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  LinearProgress,
  TextField,
  styled,
  MenuItem,
  Button,
  Alert,
} from "@mui/material";
import { mergeStoredUser, readStoredUser } from "../utils/userSession";
import LocationSearchableField from "./LocationSearchableField";

const RoundedTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "30px",
  },
});

/**
 * ✅ CRA API base
 *
 * Recommended envs:
 * - frontend/.env                REACT_APP_API_BASE=http://localhost:5000/api
 * - frontend/.env.production     REACT_APP_API_BASE=/rpo/api
 *
 * This fallback prevents production from ever hitting localhost if env is missing.
 */
const API_BASE = process.env.REACT_APP_API_BASE;

const profileAuthHeaders = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken") ||
    sessionStorage.getItem("token") || sessionStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CandidatePersonalInfo = () => {
  const { id } = useParams();
  const candidateId = parseInt(id, 10);

  const [candidate, setCandidate] = useState(null);
  const [form, setForm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [, setIsUploadingAvatar] = useState(false);

  // 🔹 hidden file input ref for avatar upload
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchCandidate = async () => {
      setError("");
      setCandidate(null);
      setForm(null);
      try {
        const response = await fetch(`${API_BASE}/api/profile/${id}`, {
          headers: profileAuthHeaders(),
          credentials: "include",
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Failed to load profile");

        setCandidate(data);

        const fullName = data.name || "";
        const nameParts = fullName.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        setForm({
          firstName,
          lastName,
          title: data.title || "",
          status: data.status || "Active",
          email: data.email || "",
          linkedin: data.linkedin || "",
          portfolio: data.portfolio || "",
          summary: data.summary || "",
          currently: data.currently || "",
          why_looking: data.why_looking || "",
          personality: data.personality || "",
          location: data.location || "",
          timezone: data.timezone || "",
          availability: data.availability || "",
          interview_activity: data.interview_activity || "",
          compensation: data.compensation || "",
          work_authorization: data.work_authorization || "",
        });
      } catch (err) {
        setError(err.message);
        console.error(err);
      }
    };

    fetchCandidate();
  }, [id]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 🔹 handle avatar upload when image is clicked & file selected
  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingAvatar(true);

      const token = localStorage.getItem("token"); // adjust if you store it differently
      const formData = new FormData();
      formData.append("profile_image", file);

      // NOTE: this endpoint in your backend is NOT under /api in your code sample.
      // If you later move it under /api, update this to `${API_BASE}/update-profile-image`.
      const res = await fetch(
        `${API_BASE}/update-profile-image`,
        {
          method: "POST",
          headers: {
            Authorization: token || "",
            // ⚠️ Do NOT set Content-Type here; browser will set multipart boundary
          },
          body: formData,
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to upload avatar");
      }

      const data = await res.json();

      // backend returns { success, profile_image: newImageUrl }
      if (data.profile_image) {
        const profileImage = data.profile_image;

        setCandidate((prev) =>
          prev
            ? {
                ...prev,
                profile_image: profileImage,
              }
            : prev
        );

        const currentUser = readStoredUser();
        const updatedUserId = Number(
          candidate?.user_id || candidate?.userId || candidate?.id || candidateId || 0
        );

        if (currentUser?.id && Number(currentUser.id) === updatedUserId) {
          mergeStoredUser({ profile_image: profileImage });
        }
      }

      // clear file input so same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Avatar upload error:", err);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!candidate || !form) return;

    try {
      setError("");
      setIsSaving(true);

      const userId =
        candidate.user_id || candidate.userId || candidate.id || candidateId;

      const payload = {
        userId,
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,

        // fields to go into user_info
        title: form.title,
        status: form.status,
        linkedin: form.linkedin,
        portfolio: form.portfolio,
        summary: form.summary,
        currently: form.currently,
        why_looking: form.why_looking,
        personality: form.personality,
        location: form.location,
        timezone: form.timezone,
        availability: form.availability,
        interview_activity: form.interview_activity,
        compensation: form.compensation,
        work_authorization: form.work_authorization,
      };

      const res = await fetch(`${API_BASE}/api/profile/${userId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...profileAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to save profile");
      }

      const updated = await res.json();

      setCandidate((prev) => ({
        ...prev,
        ...updated.user,
        ...updated.userInfo,
      }));
    } catch (err) {
      setError(err.message);
      console.error("Save error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!candidate || !form) {
    return (
      <Paper sx={{ p: 4 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="h6">Profile not found</Typography>
      </Paper>
    );
  }

  const fullName = `${form.firstName} ${form.lastName}`.trim();

  return (
    <div>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="h6" sx={{ mb: 0, fontSize: "14px", fontWeight: 400 }}>
        Profile Info
      </Typography>
      <Typography sx={{ marginTop: "10px", fontSize: "12px", fontWeight: 400 }}>
        Personal and contact details
      </Typography>

      {/* ---------- Candidate Fields ---------- */}
      <Box
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          {/* 🔹 Clickable profile avatar */}
          <img
            className="profile-avatar"
            src={candidate.profile_image || "/default-avatar.png"}
            alt={fullName}
            onClick={handleAvatarClick}
            style={{ cursor: "pointer" }}
          />

          {/* 🔹 Hidden file input for avatar */}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleAvatarChange}
          />

          <div className="sm-flex gap-30px ">
            <RoundedTextField
              variant="outlined"
              value={form.firstName}
              onChange={handleChange("firstName")}
              size="small"
              sx={{ width: "240px" }}
            />
            <RoundedTextField
              variant="outlined"
              value={form.lastName}
              onChange={handleChange("lastName")}
              size="small"
              sx={{ width: "240px" }}
            />
          </div>
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Title</Typography>
          <RoundedTextField
            variant="outlined"
            value={form.title}
            onChange={handleChange("title")}
            size="small"
            sx={{ width: "240px" }}
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Status</Typography>
          <TextField
            value={form.status}
            onChange={handleChange("status")}
            select
            fullWidth
            size="small"
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Open to work">Open to work</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </TextField>
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Email address</Typography>
          <TextField
            value={form.email}
            onChange={handleChange("email")}
            fullWidth
            size="small"
            InputProps={{
              sx: { color: "#FF6F00", fontWeight: 500 },
            }}
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>LinkedIn</Typography>
          <TextField
            value={form.linkedin}
            onChange={handleChange("linkedin")}
            fullWidth
            size="small"
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Portfolio</Typography>
          <TextField
            value={form.portfolio}
            onChange={handleChange("portfolio")}
            fullWidth
            size="small"
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Summary</Typography>
          <TextField
            className="border-radius-8px"
            value={form.summary}
            onChange={handleChange("summary")}
            fullWidth
            multiline
            rows={3}
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Currently</Typography>
          <TextField
            className="border-radius-8px"
            value={form.currently || ""}
            onChange={handleChange("currently")}
            fullWidth
            multiline
            rows={15}
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Why Looking</Typography>
          <TextField
            className="border-radius-8px"
            placeholder="Describe reasons for job search..."
            fullWidth
            value={form.why_looking || ""}
            onChange={handleChange("why_looking")}
            multiline
            rows={10}
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Personality</Typography>
          <TextField
            className="border-radius-8px"
            placeholder="Describe personality traits..."
            fullWidth
            value={form.personality || ""}
            onChange={handleChange("personality")}
            multiline
            rows={8}
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Location</Typography>
          <LocationSearchableField
            value={form.location || ""}
            onChange={(nextValue) =>
              handleChange("location")({ target: { value: nextValue } })
            }
            placeholder="Search city, state, or country"
            textFieldProps={{ label: "Location", fullWidth: true }}
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Timezone</Typography>
          <TextField
            value={form.timezone || ""}
            onChange={handleChange("timezone")}
            fullWidth
            size="small"
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Availability</Typography>
          <TextField
            className="border-radius-8px"
            value={form.availability || ""}
            onChange={handleChange("availability")}
            fullWidth
            size="small"
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Interview Activity</Typography>
          <TextField
            className="border-radius-8px"
            value={form.interview_activity || ""}
            onChange={handleChange("interview_activity")}
            fullWidth
            size="small"
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Compensation</Typography>
          <TextField
            className="border-radius-8px"
            value={form.compensation || ""}
            onChange={handleChange("compensation")}
            fullWidth
            size="small"
          />
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Work Authorization</Typography>
          <TextField
            select
            className="border-radius-8px"
            value={form.work_authorization || ""}
            onChange={handleChange("work_authorization")}
            fullWidth
            size="small"
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Availability to Interview</Typography>
          <TextField className="border-radius-8px" fullWidth size="small">
            <MenuItem value="Open">Open</MenuItem>
          </TextField>
        </Box>

        <Box className="sm-flex space-between item-center profile-row" gap={2}>
          <Typography>Documents</Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {[
              { name: "Olivia_CV_2025.pdf", progress: 100 },
              { name: "Olivia_CoverLetter_2025.pdf", progress: 100 },
            ].map((doc, i) => (
              <Box key={i}>
                <Typography variant="body2" fontWeight={500}>
                  {doc.name}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={doc.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#8A5CF6",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ---------- Save Button ---------- */}
      <Box mt={4} textAlign="right">
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#FF6F00",
            borderRadius: 6,
            px: 3,
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { backgroundColor: "#e65c00" },
          }}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </div>
  );
};

export default CandidatePersonalInfo;
